import React, { useMemo } from 'react'
import type { PermissionResource } from '../../types'
import { tx, lang } from '../../lib/i18n'

interface PermissionMatrixProps {
  resources: PermissionResource[]
  /** the ticked permissions */
  value: Set<string>
  onChange?: (next: Set<string>) => void
  readOnly?: boolean
  /** role permissions to compare against — cells that differ are highlighted (per-person view) */
  base?: Set<string>
  testIdPrefix?: string
}

const perm = (r: string, a: string) => `${r}.${a}`

/**
 * Menus × actions checkbox grid. Ticking an action also ticks "view" of that menu;
 * unticking "view" unticks every action of the menu (an action needs its page).
 */
const PermissionMatrix: React.FC<PermissionMatrixProps> = ({ resources, value, onChange, readOnly, base, testIdPrefix = 'perm' }) => {
  const th = lang === 'th'
  const groups = useMemo(() => {
    const out: { group: string; items: PermissionResource[] }[] = []
    for (const r of resources) {
      const g = out.find((x) => x.group === r.group)
      if (g) g.items.push(r)
      else out.push({ group: r.group, items: [r] })
    }
    return out
  }, [resources])

  const set = (mut: (s: Set<string>) => void) => {
    if (readOnly || !onChange) return
    const next = new Set(value)
    mut(next)
    onChange(next)
  }

  const toggle = (r: PermissionResource, action: string) =>
    set((s) => {
      const p = perm(r.key, action)
      if (s.has(p)) {
        s.delete(p)
        if (action === 'view') r.actions.forEach((a) => s.delete(perm(r.key, a.key)))
      } else {
        s.add(p)
        s.add(perm(r.key, 'view'))
      }
    })

  const toggleRow = (r: PermissionResource) =>
    set((s) => {
      const all = r.actions.every((a) => s.has(perm(r.key, a.key)))
      r.actions.forEach((a) => (all ? s.delete(perm(r.key, a.key)) : s.add(perm(r.key, a.key))))
    })

  const setAll = (on: boolean) =>
    set((s) => {
      for (const r of resources) for (const a of r.actions) on ? s.add(perm(r.key, a.key)) : s.delete(perm(r.key, a.key))
    })

  const total = resources.reduce((n, r) => n + r.actions.length, 0)

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-matrix`}>
      <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
        <span>
          {tx('Selected')} <b className="text-gray-800">{value.size}</b> / {total}
        </span>
        {!readOnly && onChange && (
          <div className="flex gap-2">
            <button type="button" onClick={() => setAll(true)} className="px-2.5 py-1 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid={`${testIdPrefix}-all`}>
              {tx('Select all')}
            </button>
            <button type="button" onClick={() => setAll(false)} className="px-2.5 py-1 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid={`${testIdPrefix}-none`}>
              {tx('Clear all')}
            </button>
          </div>
        )}
      </div>
      {base && (
        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-green-100 border border-green-300" /> {tx('Added for this person')}</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-red-100 border border-red-300" /> {tx('Removed for this person')}</span>
        </div>
      )}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 w-56">{tx('Menu')}</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">{tx('Allowed actions')}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g.group}>
                <tr className="bg-gray-50/60">
                  <td colSpan={2} className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{g.group}</td>
                </tr>
                {g.items.map((r) => {
                  const allOn = r.actions.every((a) => value.has(perm(r.key, a.key)))
                  const someOn = r.actions.some((a) => value.has(perm(r.key, a.key)))
                  return (
                    <tr key={r.key} className="border-t border-gray-100 align-top" data-testid={`${testIdPrefix}-row-${r.key}`}>
                      <td className="px-4 py-2.5">
                        <label className={`flex items-center gap-2 ${readOnly ? '' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={allOn}
                            ref={(el) => {
                              if (el) el.indeterminate = someOn && !allOn
                            }}
                            disabled={readOnly}
                            onChange={() => toggleRow(r)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                            data-testid={`${testIdPrefix}-rowall-${r.key}`}
                          />
                          <span className="font-medium text-gray-800">{th ? r.labelTh : r.label}</span>
                        </label>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1.5">
                          {r.actions.map((a) => {
                            const p = perm(r.key, a.key)
                            const on = value.has(p)
                            const diff = base ? (on && !base.has(p) ? 'added' : !on && base.has(p) ? 'removed' : null) : null
                            return (
                              <label
                                key={a.key}
                                title={p}
                                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs select-none ${
                                  diff === 'added'
                                    ? 'bg-green-50 border-green-300'
                                    : diff === 'removed'
                                      ? 'bg-red-50 border-red-300 line-through'
                                      : on
                                        ? 'bg-indigo-50 border-indigo-200'
                                        : 'bg-white border-gray-200'
                                } ${readOnly ? '' : 'cursor-pointer hover:border-indigo-300'}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  disabled={readOnly}
                                  onChange={() => toggle(r, a.key)}
                                  className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600"
                                  data-testid={`${testIdPrefix}-${p}`}
                                />
                                <span className={a.danger ? 'text-red-700' : 'text-gray-700'}>{th ? a.labelTh : a.label}</span>
                              </label>
                            )
                          })}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400">{tx('Red actions change real data or money — grant them carefully. Every change is recorded in the audit log.')}</p>
    </div>
  )
}

export default PermissionMatrix
